import { useEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { useAuth } from '../../hooks'
import { ApiError } from '../../services'

interface UseSignInModalArgs {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  t: TFunction<'auth'>
}

export interface SignInModalState {
  email: string
  password: string
  showPassword: boolean
  error: string | null
  isLoading: boolean
  modalRef: React.RefObject<HTMLDivElement>
  emailRef: React.RefObject<HTMLInputElement>
  setEmail: (value: string) => void
  setPassword: (value: string) => void
  toggleShowPassword: () => void
  handleSubmit: (event: React.FormEvent) => Promise<void>
  handleBackdropClick: (event: React.MouseEvent) => void
}

function useFocusEmailWhenOpen(isOpen: boolean, emailRef: React.RefObject<HTMLInputElement>): void {
  useEffect(() => {
    if (isOpen && emailRef.current) {
      setTimeout(() => emailRef.current?.focus(), 100)
    }
  }, [isOpen, emailRef])
}

function useEscapeToClose(isOpen: boolean, isLoading: boolean, onClose: () => void): void {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && isOpen && !isLoading) onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isLoading, onClose])
}

function useLockBodyScroll(isOpen: boolean): void {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])
}

export function useSignInModal({
  isOpen,
  onClose,
  onSuccess,
  t,
}: UseSignInModalArgs): SignInModalState {
  const { login, isLoading } = useAuth()
  const modalRef = useRef<HTMLDivElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useFocusEmailWhenOpen(isOpen, emailRef)
  useEscapeToClose(isOpen, isLoading, onClose)
  useLockBodyScroll(isOpen)

  useEffect(() => {
    if (!isOpen) {
      setEmail('')
      setPassword('')
      setError(null)
      setShowPassword(false)
    }
  }, [isOpen])

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setError(null)
    if (!email || !password) {
      setError(t('login.validationError'))
      return
    }
    try {
      await login({ email, password })
      onSuccess()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('login.unexpectedError'))
    }
  }

  const handleBackdropClick = (event: React.MouseEvent): void => {
    if (event.target === event.currentTarget && !isLoading) onClose()
  }

  const toggleShowPassword = (): void => {
    setShowPassword((value) => !value)
  }

  return {
    email,
    password,
    showPassword,
    error,
    isLoading,
    modalRef,
    emailRef,
    setEmail,
    setPassword,
    toggleShowPassword,
    handleSubmit,
    handleBackdropClick,
  }
}
